import { useEffect } from 'react'

// ─── Compteur global de modales ouvertes ─────────────────────────────────────
// Plusieurs modales peuvent se chevaucher (rare mais possible). On verrouille
// le scroll uniquement quand la première s'ouvre, on le restaure quand la
// dernière se ferme. Évite les conflits si plusieurs composants utilisent
// useScrollLock en parallèle.
let lockCount = 0
let savedScrollY = 0
let savedBodyStyles: {
  position: string
  top: string
  left: string
  right: string
  width: string
  overflow: string
} | null = null

function applyLock() {
  if (typeof window === 'undefined') return
  savedScrollY = window.scrollY || document.documentElement.scrollTop || 0

  savedBodyStyles = {
    position: document.body.style.position,
    top:      document.body.style.top,
    left:     document.body.style.left,
    right:    document.body.style.right,
    width:    document.body.style.width,
    overflow: document.body.style.overflow,
  }

  // Fixe le body à l'écran et empêche tout scroll natif (y compris le
  // bounce iOS Safari). On compense en repositionnant top négativement
  // pour conserver visuellement la position de scroll d'origine.
  document.body.style.position = 'fixed'
  document.body.style.top      = `-${savedScrollY}px`
  document.body.style.left     = '0'
  document.body.style.right    = '0'
  document.body.style.width    = '100%'
  document.body.style.overflow = 'hidden'
}

function releaseLock() {
  if (typeof window === 'undefined' || savedBodyStyles === null) return
  document.body.style.position = savedBodyStyles.position
  document.body.style.top      = savedBodyStyles.top
  document.body.style.left     = savedBodyStyles.left
  document.body.style.right    = savedBodyStyles.right
  document.body.style.width    = savedBodyStyles.width
  document.body.style.overflow = savedBodyStyles.overflow
  // Restaurer la position de scroll d'origine
  window.scrollTo(0, savedScrollY)
  savedBodyStyles = null
}

/**
 * Verrouille le scroll de la page tant que le composant est monté (et active).
 * - Appelle au mount, restaure à l'unmount
 * - Compatible avec plusieurs modales chevauchées (compteur global)
 * - Empêche le scroll bounce iOS via `position: fixed` sur le body
 * - Restaure exactement la position de scroll précédente à la fermeture
 *
 * @param active — false pour désactiver le lock sans démonter le hook
 */
export function useScrollLock(active: boolean = true): void {
  useEffect(() => {
    if (!active) return
    if (lockCount === 0) applyLock()
    lockCount += 1
    return () => {
      lockCount = Math.max(0, lockCount - 1)
      if (lockCount === 0) releaseLock()
    }
  }, [active])
}
